"use client";

import { SITE_CONFIG } from "../../lib/site-config";

export default function OnlineDeliveryClient() {
  return (
    <main className="max-w-5xl mx-auto px-4 py-14">
      {/* ================= HEADER ================= */}
      <div className="mb-14 text-center">
        <h1 className="text-4xl font-semibold tracking-tight">
          Online Delivery Information
        </h1>
        <p className="text-sm text-gray-500 mt-3">
          Last updated: 24 December 2025
        </p>
      </div>

      {/* ================= CONTENT ================= */}
      <div className="space-y-8">
        {/* INTRO */}
        <section className="bg-white border rounded-2xl p-8 text-sm text-gray-700 leading-relaxed">
          <p>
            This Online Delivery Policy explains how products purchased from{" "}
            <strong>{SITE_CONFIG.name}</strong> are processed, shipped,
            and delivered. By placing an order, you agree to the delivery terms
            described below.
          </p>
        </section>

        <PolicyCard title="1. Delivery Coverage">
          <ul className="list-disc list-inside space-y-1">
            <li>We deliver products across Bangladesh.</li>
            <li>
              Certain remote or restricted areas may have limited delivery
              availability.
            </li>
            <li>
              International delivery may be offered on selected products only
              (subject to additional charges and regulations).
            </li>
          </ul>
        </PolicyCard>

        <PolicyCard title="2. Order Processing Time">
          <ul className="list-disc list-inside space-y-1">
            <li>
              Orders are typically processed within <strong>24–48 hours</strong>{" "}
              after confirmation.
            </li>
            <li>
              Processing time may be longer during peak seasons, holidays, or
              promotional campaigns.
            </li>
            <li>
              Orders placed on weekends or public holidays will be processed on
              the next working day.
            </li>
          </ul>
        </PolicyCard>

        <PolicyCard title="3. Estimated Delivery Time">
          <ul className="list-disc list-inside space-y-1">
            <li>
              Inside Dhaka: <strong>1–3 business days</strong>
            </li>
            <li>
              Outside Dhaka: <strong>3–7 business days</strong>
            </li>
            <li>
              Delivery timelines are estimates and may vary due to external
              factors.
            </li>
          </ul>
        </PolicyCard>

        <PolicyCard title="4. Delivery Charges">
          <ul className="list-disc list-inside space-y-1">
            <li>
              Delivery charges vary based on location, product size, and weight.
            </li>
            <li>
              Any applicable delivery fee will be clearly shown during checkout.
            </li>
            <li>
              Promotional free-delivery offers may apply to selected products or
              order values.
            </li>
          </ul>
        </PolicyCard>

        <PolicyCard title="5. Courier & Logistics Partners">
          <p>
            We use trusted third-party courier and logistics partners to ensure
            safe and timely delivery. Once an order is handed over to the
            courier, delivery timelines are subject to the courier’s service
            standards.
          </p>
        </PolicyCard>

        <PolicyCard title="6. Order Tracking">
          <ul className="list-disc list-inside space-y-1">
            <li>
              Tracking information may be provided via SMS, email, or your
              account dashboard.
            </li>
            <li>
              Tracking updates depend on courier system availability.
            </li>
          </ul>
        </PolicyCard>

        <PolicyCard title="7. Delivery Attempt & Receiving Orders">
          <ul className="list-disc list-inside space-y-1">
            <li>
              Customers or authorized recipients must be available to receive
              the order.
            </li>
            <li>
              If delivery fails due to unavailability, re-delivery charges may
              apply.
            </li>
            <li>
              Valid identification or proof of order may be required upon
              delivery.
            </li>
          </ul>
        </PolicyCard>

        <PolicyCard title="8. Damaged or Missing Items During Delivery">
          <p>
            Customers are advised to inspect the package at the time of delivery.
            If any item is damaged, missing, or incorrect, please notify us
            within <strong>24 hours</strong> with clear photos or video
            evidence.
          </p>
        </PolicyCard>

        <PolicyCard title="9. Delays & Force Majeure">
          <p>
            We are not responsible for delivery delays caused by natural
            disasters, political unrest, strikes, courier disruptions, or other
            circumstances beyond our control.
          </p>
        </PolicyCard>

        <PolicyCard title="10. In-Store Pickup">
          <p>
            Selected products may be eligible for in-store pickup. Customers
            will be notified when the order is ready for collection. Valid proof
            of purchase is required.
          </p>
        </PolicyCard>

        <PolicyCard title="11. Policy Updates">
          <p>
            We reserve the right to update this Online Delivery Policy at any
            time. Updates will be effective immediately upon posting on this
            page.
          </p>
        </PolicyCard>

        {/* CONTACT SECTION */}
        <section className="border rounded-2xl p-8 bg-gray-50">
          <h2 className="text-lg font-semibold mb-3">Delivery Support</h2>
          <p className="text-sm text-gray-700">
            For delivery-related questions or concerns, please contact us:
          </p>

          <p className="mt-4 text-sm leading-relaxed">
            <strong>{SITE_CONFIG.name}</strong>
            <br />
            Email: {SITE_CONFIG.email}
            <br />
            Phone: {SITE_CONFIG.phone}
            <br />
            Address: {SITE_CONFIG.address}
          </p>
        </section>
      </div>
    </main>
  );
}

/* ================= REUSABLE CARD ================= */
function PolicyCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white border rounded-2xl p-8 space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}